import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  const d = typeof iso === "string" ? new Date(iso) : new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const dy = Math.floor(h / 24);
  return `${dy}d ago`;
};

export const BlessingsSection = () => {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/blessings`);
      setItems(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const n = name.trim();
    const m = message.trim();
    if (!n || !m) {
      toast.error("Please share your name and a short blessing.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/blessings`, { name: n, message: m });
      setItems((prev) => [data, ...prev]);
      setName("");
      setMessage("");
      toast.success("Thank you for your blessing.");
    } catch (err) {
      toast.error("Could not send your blessing. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.section
      className="sec snap-sec"
      data-testid="blessings-section"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8 }}
    >
      <img
        src="/assets/ganesha.png"
        alt=""
        style={{ width: 84, margin: "0 auto 10px", display: "block", opacity: 0.9 }}
      />
      <div className="sec-title f-corm">Blessings &amp; Wishes</div>
      <div className="rsvp-note f-corm">
        Your kind words mean the world to us.<br />
        Leave a little blessing for our new beginning.
      </div>

      <form className="bl-form f-corm" onSubmit={submit} data-testid="blessing-form">
        <input
          className="bl-input"
          type="text"
          placeholder="Your name"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          data-testid="blessing-name-input"
        />
        <textarea
          className="bl-textarea"
          rows={3}
          placeholder="Write a short blessing or wish for the couple..."
          value={message}
          maxLength={280}
          onChange={(e) => setMessage(e.target.value)}
          data-testid="blessing-message-input"
        />
        <div className="bl-row">
          <span className="bl-count">{message.length}/280</span>
          <button
            className="bl-submit"
            type="submit"
            disabled={busy}
            data-testid="blessing-submit-btn"
          >
            {busy ? "Sending…" : "Send Blessing"}
          </button>
        </div>
      </form>

      <div className="bl-wall" data-testid="blessings-wall">
        {loading && (
          <div className="bl-empty f-corm">Loading blessings…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="bl-empty f-corm">
            Be the first to leave a blessing for the couple.
          </div>
        )}
        <AnimatePresence initial={false}>
          {items.map((b) => (
            <motion.div
              key={b.id}
              className="bl-card"
              data-testid="blessing-card"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
            >
              <div className="bl-msg f-corm">&ldquo;{b.message}&rdquo;</div>
              <div className="bl-meta f-corm">
                <span className="bl-name">— {b.name}</span>
                <span className="bl-time">{timeAgo(b.timestamp)}</span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.section>
  );
};
